import React, { useState } from "react";
import Box from "@mui/material/Box";
import { motion } from "framer-motion";
import { useEffect } from "react";
import ChatMessages from "./ChatMessages";
import { s3baseUrl } from "src/config/config";
import { getTimeBasedOnDate } from "src/utils/constants";
import { gr } from "src/design-system/tokens";
import icons from "src/design-system/icons";
import GrIcon from "src/design-system/components/GrIcon";
import GrIconButton from "src/design-system/components/GrIconButton";
import GrPresenceAvatar from "src/design-system/components/GrPresenceAvatar";
import { grCleanName } from "src/design-system/components/GrChatListRow";

const headSx = {
  display: "flex",
  alignItems: "center",
  gap: gr.space[2],
  px: gr.space[3],
  py: gr.space[2],
  cursor: "pointer",
  fontFamily: gr.font.family,
  color: gr.color.ink2,
  background: gr.color.surface,
  borderBottom: `1px solid ${gr.color.line}`,
  borderRadius: `${gr.radius.tile} ${gr.radius.tile} 0 0`,
  "&:hover": { background: gr.color.tint },
};

const nameSx = {
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  fontSize: gr.font.size.md,
  fontWeight: gr.font.weight.semibold,
  color: gr.color.ink,
  textTransform: "capitalize",
};

const timeSx = { fontSize: gr.font.size.xs, color: gr.color.muted };

function ChatBox(props) {
  const { selectedChat, selectedUser, handleCloseChat } = props;
  const [isExpandedChat, setIsExpandedChat] = useState(false);

  const bottom_in_chat = {
    hidden: {
      y: "100px",
      x: "0px",
      height: "0",
      opacity: 1,
      scale: 1,
    },
    visible: {
      opacity: 1,
      scale: 1,
      height: "67vh",
      y: "0px",
      x: "0px",
      transition: { delay: 0, duration: 0.5 },
    },
  };

  const profile = selectedUser ? selectedUser : selectedChat?.receiver_profile;

  useEffect(() => {
    setIsExpandedChat(true);
  }, [selectedChat, selectedUser]);

  return (
    <>
      {/* Header toggles the box; keyboard: Enter/Space, with aria-expanded for screen readers. */}
      <Box
        sx={headSx}
        role="button"
        tabIndex={0}
        aria-expanded={isExpandedChat}
        onClick={() => setIsExpandedChat(!isExpandedChat)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setIsExpandedChat(!isExpandedChat);
          }
        }}
      >
        <GrPresenceAvatar
          src={s3baseUrl + profile?.profileImage}
          name={grCleanName(profile?.name)}
          online={!!profile?.isOnline}
          size="sm"
        />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={nameSx}>{grCleanName(profile?.name)}</Box>
          <Box sx={timeSx}>
            {getTimeBasedOnDate(
              profile?.lastMessageDateTime,
              "DD MMM YYYY [,] hh:mm A"
            )}
          </Box>
        </Box>
        <GrIcon icon={isExpandedChat ? icons.chevronDown : icons.chevronUp} />
        <GrIconButton
          variant="ghost"
          size="sm"
          aria-label="Close chat"
          onClick={(e) => {
            // Don't also toggle the header while closing.
            e.stopPropagation();
            handleCloseChat();
          }}
        >
          <GrIcon icon={icons.close} />
        </GrIconButton>
      </Box>
      <motion.div
        className="gr-chat-box-panel"
        variants={bottom_in_chat}
        initial="hidden"
        animate={isExpandedChat ? "visible" : "hidden"}
        exit="hidden"
      >
        <ChatMessages is_popup={true} isExpandedChat={isExpandedChat} />
      </motion.div>
    </>
  );
}

export default ChatBox;
